import { useMemo } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Check, X } from 'lucide-react';
import { usePurchaseOrderStore, useSupplierStore, useProductStore } from '../../hooks';
import { Button, Card, CardContent, useToast, Badge } from '@lancar-pos/ui';

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'danger' | 'primary'> = {
  draft: 'default',
  pending: 'warning',
  confirmed: 'primary',
  received: 'success',
  cancelled: 'danger',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  pending: 'Menunggu',
  confirmed: 'Dikonfirmasi',
  received: 'Diterima',
  cancelled: 'Dibatalkan',
};

export function PurchaseOrderDetailPage() {
  const navigate = useNavigate();
  const { orderId } = useParams({ from: '/app/inventory/purchase-orders/$orderId' });
  
  const { purchaseOrders, updateStatus, receiveGoods } = usePurchaseOrderStore();
  const { suppliers } = useSupplierStore();
  const { products } = useProductStore();
  const toast = useToast();

  const order = useMemo(() => {
    return purchaseOrders.find((po) => po.id === orderId);
  }, [purchaseOrders, orderId]);

  const supplier = useMemo(() => {
    if (!order) return null;
    return suppliers.find((s) => s.id === order.supplierId);
  }, [order, suppliers]);

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Pesanan tidak ditemukan</p>
      </div>
    );
  }

  const getProductName = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    return product?.name || '-';
  };

  const handleStatusChange = async (newStatus: typeof order.status) => {
    try {
      await updateStatus(order.id, newStatus);
      toast.success(`Status pesanan diperbarui menjadi ${statusLabels[newStatus]}`);
    } catch (error) {
      toast.error('Gagal memperbarui status');
    }
  };

  const handleReceive = async () => {
    const items = order.items.map((item) => ({
      ...item,
      receivedQuantity: item.quantity,
    }));
    
    try {
      await receiveGoods(order.id, items);
      toast.success('Barang diterima dan stok diperbarui');
    } catch (error) {
      toast.error('Gagal menerima barang');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate({ to: '/app/inventory/purchase-orders' })}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">{order.orderNumber}</h1>
            <Badge variant={statusColors[order.status]}>{statusLabels[order.status]}</Badge>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-6 space-y-4">
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">Informasi Pesanan</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Supplier:</span>
                <p className="font-medium">{supplier?.name || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Tanggal Dibuat:</span>
                <p className="font-medium">{new Date(order.createdAt).toLocaleDateString('id-ID')}</p>
              </div>
              {order.expectedDate && (
                <div>
                  <span className="text-gray-500">Tanggal Diharapkan:</span>
                  <p className="font-medium">{new Date(order.expectedDate).toLocaleDateString('id-ID')}</p>
                </div>
              )}
              {order.receivedDate && (
                <div>
                  <span className="text-gray-500">Tanggal Diterima:</span>
                  <p className="font-medium">{new Date(order.receivedDate).toLocaleDateString('id-ID')}</p>
                </div>
              )}
              {order.notes && (
                <div className="col-span-2">
                  <span className="text-gray-500">Catatan:</span>
                  <p className="font-medium">{order.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">Daftar Produk</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Produk</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Harga</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {order.items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-2">{getProductName(item.productId)}</td>
                      <td className="px-4 py-2 text-right">{item.quantity}</td>
                      <td className="px-4 py-2 text-right">Rp {item.unitPrice.toLocaleString('id-ID')}</td>
                      <td className="px-4 py-2 text-right font-medium">Rp {item.totalPrice.toLocaleString('id-ID')}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t border-gray-200">
                  <tr>
                    <td colSpan={3} className="px-4 py-2 text-right font-semibold">Total</td>
                    <td className="px-4 py-2 text-right font-bold">Rp {order.totalAmount.toLocaleString('id-ID')}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">Aksi</h2>
            <div className="flex gap-3 flex-wrap">
              {order.status === 'draft' && (
                <>
                  <Button onClick={() => handleStatusChange('pending')}>
                    <Check className="w-4 h-4 mr-2" />Kirim
                  </Button>
                  <Button variant="danger" onClick={() => handleStatusChange('cancelled')}>
                    <X className="w-4 h-4 mr-2" />Batal
                  </Button>
                </>
              )}
              {order.status === 'pending' && (
                <>
                  <Button onClick={() => handleStatusChange('confirmed')}>
                    <Check className="w-4 h-4 mr-2" />Konfirmasi
                  </Button>
                  <Button variant="danger" onClick={() => handleStatusChange('cancelled')}>
                    <X className="w-4 h-4 mr-2" />Batal
                  </Button>
                </>
              )}
              {order.status === 'confirmed' && (
                <Button onClick={handleReceive}>
                  <Check className="w-4 h-4 mr-2" />Terima Barang
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
